import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Heart, Users, Leaf, Shield, TrendingUp, Baby } from 'lucide-react';

const About = () => {
  const { themeColors } = useTheme();

  const containerStyle = {
    minHeight: 'calc(100vh - 120px)',
    backgroundColor: themeColors.background,
    padding: '40px 20px'
  };

  const contentStyle = {
    maxWidth: '1000px',
    margin: '0 auto'
  };

  const headerStyle = {
    textAlign: 'center',
    marginBottom: '60px'
  };

  const titleStyle = {
    color: themeColors.text,
    fontSize: '48px',
    fontWeight: 'bold',
    marginBottom: '20px'
  };

  const subtitleStyle = {
    color: themeColors.textSecondary,
    fontSize: '20px',
    lineHeight: '1.6',
    maxWidth: '700px',
    margin: '0 auto'
  };

  const sectionStyle = {
    marginBottom: '60px'
  };

  const sectionTitleStyle = {
    color: themeColors.text,
    fontSize: '24px',
    fontWeight: 'bold',
    marginBottom: '16px'
  };

  const cardStyle = {
    backgroundColor: themeColors.cardBackground,
    borderRadius: '16px',
    padding: '32px',
    marginBottom: '24px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
  };

  const paragraphStyle = {
    color: themeColors.textSecondary,
    fontSize: '16px',
    lineHeight: '1.8',
    marginBottom: '16px'
  };

  const valuesGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '24px',
    marginBottom: '24px'
  };

  const valueCardStyle = {
    backgroundColor: themeColors.cardBackground,
    borderRadius: '16px',
    padding: '32px',
    textAlign: 'center',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
  };

  const iconWrapperStyle = {
    width: '64px',
    height: '64px',
    borderRadius: '50%',
    backgroundColor: themeColors.primary + '15',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 16px'
  };

  const statsGridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '24px'
  };

  const statCardStyle = {
    backgroundColor: themeColors.background,
    borderRadius: '12px',
    padding: '24px',
    textAlign: 'center',
    border: `1px solid ${themeColors.border}`
  };

  const statNumberStyle = {
    color: themeColors.primary,
    fontSize: '36px',
    fontWeight: 'bold',
    marginBottom: '8px'
  };

  const statLabelStyle = {
    color: themeColors.textSecondary,
    fontSize: '14px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  };

  return (
    <div style={containerStyle}>
      <div style={contentStyle}>
        {/* Header */}
        <div style={headerStyle}>
          <h1 style={titleStyle}>About BabyResell</h1>
          <p style={subtitleStyle}>
            Making quality baby items accessible to every family while reducing waste and building community.
          </p>
        </div>

        {/* Our Story */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Our Story</h2>
          <div style={cardStyle}>
            <p style={paragraphStyle}>
              BabyResell was born from a simple observation: babies grow fast, and parents spend thousands of dollars on items that are used for just a few months. Meanwhile, gently used baby items sit in closets and garages, waiting for their next adventure.
            </p>
            <p style={paragraphStyle}>
              We created BabyResell to solve both problems. Our platform connects parents who want to sell their pre-loved baby items with families looking for quality products at affordable prices. It's a win-win for your wallet and the planet.
            </p>
            <p style={{ ...paragraphStyle, marginBottom: 0 }}>
              BabyResell is a product of Interverse, and we're committed to building tools that make a real difference in people's lives. Every transaction on our platform helps a family save money, reduces waste, and strengthens our community of parents supporting parents.
            </p>
          </div>
        </div>

        {/* Our Mission */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Our Mission</h2>
          <div style={cardStyle}>
            <p style={{ ...paragraphStyle, fontSize: '18px', fontWeight: '500', color: themeColors.text }}>
              To make parenting more affordable and sustainable by creating a trusted marketplace where families can buy and sell quality pre-loved baby items with confidence.
            </p>
          </div>
        </div>

        {/* Our Values */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>Our Values</h2>
          <div style={valuesGridStyle}>
            <div style={valueCardStyle}>
              <div style={iconWrapperStyle}>
                <Heart size={32} color={themeColors.primary} />
              </div>
              <h3 style={{ color: themeColors.text, fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                Families First
              </h3>
              <p style={{ color: themeColors.textSecondary, fontSize: '15px', lineHeight: '1.6' }}>
                Every decision we make is guided by what's best for the families we serve. Your success is our success.
              </p>
            </div>

            <div style={valueCardStyle}>
              <div style={iconWrapperStyle}>
                <Leaf size={32} color={themeColors.primary} />
              </div>
              <h3 style={{ color: themeColors.text, fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                Sustainability
              </h3>
              <p style={{ color: themeColors.textSecondary, fontSize: '15px', lineHeight: '1.6' }}>
                By extending the life of baby products, we reduce waste and help create a healthier planet for the next generation.
              </p>
            </div>

            <div style={valueCardStyle}>
              <div style={iconWrapperStyle}>
                <Shield size={32} color={themeColors.primary} />
              </div>
              <h3 style={{ color: themeColors.text, fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                Safety & Trust
              </h3>
              <p style={{ color: themeColors.textSecondary, fontSize: '15px', lineHeight: '1.6' }}>
                We've built safety guidelines, buyer protection, and seller verification into the platform to keep our community safe.
              </p>
            </div>

            <div style={valueCardStyle}>
              <div style={iconWrapperStyle}>
                <Users size={32} color={themeColors.primary} />
              </div>
              <h3 style={{ color: themeColors.text, fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                Community
              </h3>
              <p style={{ color: themeColors.textSecondary, fontSize: '15px', lineHeight: '1.6' }}>
                We're building more than a marketplace — we're creating a community of parents helping parents navigate parenthood together.
              </p>
            </div>

            <div style={valueCardStyle}>
              <div style={iconWrapperStyle}>
                <TrendingUp size={32} color={themeColors.primary} />
              </div>
              <h3 style={{ color: themeColors.text, fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                Transparency
              </h3>
              <p style={{ color: themeColors.textSecondary, fontSize: '15px', lineHeight: '1.6' }}>
                Clear fees, honest product descriptions, and open communication. We believe trust is built through transparency.
              </p>
            </div>

            <div style={valueCardStyle}>
              <div style={iconWrapperStyle}>
                <Baby size={32} color={themeColors.primary} />
              </div>
              <h3 style={{ color: themeColors.text, fontSize: '20px', fontWeight: 'bold', marginBottom: '12px' }}>
                Quality
              </h3>
              <p style={{ color: themeColors.textSecondary, fontSize: '15px', lineHeight: '1.6' }}>
                Pre-loved doesn't mean low quality. We encourage honest condition ratings so families can shop with confidence.
              </p>
            </div>
          </div>
        </div>

        {/* By the Numbers */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>By the Numbers</h2>
          <div style={cardStyle}>
            <div style={statsGridStyle}>
              <div style={statCardStyle}>
                <div style={statNumberStyle}>$500+</div>
                <div style={statLabelStyle}>Average Savings</div>
              </div>
              <div style={statCardStyle}>
                <div style={statNumberStyle}>70%</div>
                <div style={statLabelStyle}>Less Waste</div>
              </div>
              <div style={statCardStyle}>
                <div style={statNumberStyle}>Safe</div>
                <div style={statLabelStyle}>Buyer Protection</div>
              </div>
              <div style={statCardStyle}>
                <div style={statNumberStyle}>Community</div>
                <div style={statLabelStyle}>Parent-to-Parent</div>
              </div>
            </div>
          </div>
        </div>

        {/* What We Do */}
        <div style={sectionStyle}>
          <h2 style={sectionTitleStyle}>What We Do</h2>
          <div style={cardStyle}>
            <p style={paragraphStyle}>
              <strong style={{ color: themeColors.text }}>For Buyers:</strong> We make it easy to find quality pre-loved baby items at great prices. Our search and filter tools help you discover exactly what you need, and our buyer protection ensures you get what you paid for.
            </p>
            <p style={paragraphStyle}>
              <strong style={{ color: themeColors.text }}>For Sellers:</strong> We provide a simple platform to declutter and earn money from items your family has outgrown. List in minutes, ship when sold, and get paid securely through Stripe.
            </p>
            <p style={{ ...paragraphStyle, marginBottom: 0 }}>
              <strong style={{ color: themeColors.text }}>For Everyone:</strong> We're building a trusted community where parents can connect, share resources, and support each other through the journey of parenthood.
            </p>
          </div>
        </div>

        {/* Join Us */}
        <div style={sectionStyle}>
          <div style={{
            ...cardStyle,
            background: `linear-gradient(135deg, ${themeColors.primary}15 0%, ${themeColors.accent}15 100%)`,
            border: `2px solid ${themeColors.primary}30`,
            textAlign: 'center'
          }}>
            <h2 style={{ color: themeColors.text, fontSize: '28px', fontWeight: 'bold', marginBottom: '16px' }}>
              Join the BabyResell Community
            </h2>
            <p style={{ ...paragraphStyle, maxWidth: '600px', margin: '0 auto 24px' }}>
              Whether you're buying, selling, or both — you're helping build a more sustainable and affordable future for families everywhere.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a
                href="/register"
                style={{
                  backgroundColor: themeColors.primary,
                  color: '#ffffff',
                  padding: '14px 32px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontSize: '16px',
                  fontWeight: '600',
                  display: 'inline-block',
                  transition: 'opacity 0.2s'
                }}
                onMouseOver={(e) => e.target.style.opacity = '0.9'}
                onMouseOut={(e) => e.target.style.opacity = '1'}
              >
                Get Started
              </a>
              <a
                href="/contact"
                style={{
                  backgroundColor: themeColors.cardBackground,
                  color: themeColors.text,
                  padding: '14px 32px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontSize: '16px',
                  fontWeight: '600',
                  display: 'inline-block',
                  border: `2px solid ${themeColors.border}`,
                  transition: 'opacity 0.2s'
                }}
                onMouseOver={(e) => e.target.style.opacity = '0.9'}
                onMouseOut={(e) => e.target.style.opacity = '1'}
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
